import { db } from './firebase'
import { doc, getDoc, setDoc, updateDoc, collection, query, where, getDocs, serverTimestamp, arrayUnion, arrayRemove } from 'firebase/firestore'

class GroupAccessService {
  constructor() {
    this.groupAccessCollection = 'group_access'
  }

  // Grant access to a private group
  async grantGroupAccess(userId, organizationId, groupId, groupName, grantedBy = 'system') {
    try {
      const accessId = `${userId}_${organizationId}_${groupId}`
      const accessRef = doc(db, this.groupAccessCollection, accessId)
      
      const groupAccess = {
        userId,
        organizationId,
        groupId,
        groupName,
        grantedBy,
        grantedAt: serverTimestamp(),
        isActive: true
      }

      await setDoc(accessRef, groupAccess)
      console.log(`✅ Group access granted to user ${userId} for group ${groupName}`)
      
      return groupAccess
    } catch (error) {
      console.error('❌ Error granting group access:', error)
      throw error
    }
  }

  // Revoke access to a private group
  async revokeGroupAccess(userId, organizationId, groupId) {
    try {
      const accessId = `${userId}_${organizationId}_${groupId}`
      const accessRef = doc(db, this.groupAccessCollection, accessId)
      
      await updateDoc(accessRef, {
        isActive: false,
        revokedAt: serverTimestamp()
      })
      
      console.log(`✅ Group access revoked for user ${userId} for group ${groupId}`)
      return true
    } catch (error) {
      console.error('❌ Error revoking group access:', error)
      throw error
    }
  }

  // Check if user has access to a group
  async hasGroupAccess(userId, organizationId, groupId) {
    try {
      const accessId = `${userId}_${organizationId}_${groupId}`
      const accessRef = doc(db, this.groupAccessCollection, accessId)
      const accessDoc = await getDoc(accessRef)
      
      if (!accessDoc.exists()) {
        return false
      }
      
      const accessData = accessDoc.data()
      return accessData.isActive === true
    } catch (error) {
      console.error('❌ Error checking group access:', error)
      return false
    }
  }

  // Get all groups a user has access to for an organization
  async getUserAccessibleGroups(userId, organizationId) {
    try {
      const accessQuery = query(
        collection(db, this.groupAccessCollection),
        where('userId', '==', userId),
        where('organizationId', '==', organizationId),
        where('isActive', '==', true)
      )
      
      const accessSnapshot = await getDocs(accessQuery)
      const accessibleGroups = accessSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }))
      
      return accessibleGroups
    } catch (error) {
      console.error('❌ Error getting user accessible groups:', error)
      return []
    }
  }

  // Get all users who have access to a specific group
  async getGroupAccessUsers(organizationId, groupId) {
    try {
      const accessQuery = query(
        collection(db, this.groupAccessCollection),
        where('organizationId', '==', organizationId),
        where('groupId', '==', groupId),
        where('isActive', '==', true)
      )
      
      const accessSnapshot = await getDocs(accessQuery)
      const accessUsers = accessSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }))
      
      return accessUsers
    } catch (error) {
      console.error('❌ Error getting group access users:', error)
      return []
    }
  }

  // Filter groups based on user access (public groups + private groups user has access to)
  async filterAccessibleGroups(userId, organizationId, groups, userProfile = null) {
    try {
      if (!userId) {
        // If no user, only return public groups
        return groups.filter(group => group.isPublic === true && group.isPrivate !== true)
      }

      // Check if user is organization admin - they should see ALL groups in their organization
      const isOrganizationAdmin = userProfile?.isOrganizationAdmin || userProfile?.isPlatformAdmin
      
      if (isOrganizationAdmin) {
        console.log(`🔍 User is organization admin - showing ALL groups (${groups.length} total)`)
        return groups
      }

      const accessibleGroups = []
      
      for (const group of groups) {
        console.log(`🔍 Checking group access for: ${group.name}`, {
          isPublic: group.isPublic,
          isPrivate: group.isPrivate,
          groupId: group.id
        })
        
        // Always include public groups (isPublic === true OR isPrivate !== true)
        if (group.isPublic === true || group.isPrivate !== true) {
          accessibleGroups.push(group)
          console.log(`✅ Added public group: ${group.name}`)
        } 
        // For private groups (isPrivate === true), check if user has access
        else if (group.isPrivate === true) {
          const hasAccess = await this.hasGroupAccess(userId, organizationId, group.id)
          console.log(`🔍 Private group ${group.name} - hasAccess: ${hasAccess}`)
          if (hasAccess) {
            accessibleGroups.push(group)
            console.log(`✅ Added private group with access: ${group.name}`)
          } else {
            console.log(`❌ Excluded private group without access: ${group.name}`)
          }
        }
        // If both isPublic and isPrivate are undefined/null, treat as public for backward compatibility
        else {
          accessibleGroups.push(group)
          console.log(`✅ Added group (backward compatibility): ${group.name}`)
        }
      }
      
      console.log(`🔍 Final accessible groups: ${accessibleGroups.length} groups`)
      return accessibleGroups
    } catch (error) {
      console.error('❌ Error filtering accessible groups:', error)
      // On error, return only public groups
      return groups.filter(group => group.isPublic === true && group.isPrivate !== true)
    }
  }

  // Grant access to all users in an organization for a group (useful when making a group public)
  async grantAccessToAllOrgUsers(organizationId, groupId, groupName) {
    try {
      // Get all users who follow this organization
      const usersQuery = query(
        collection(db, 'users'),
        where('followedOrganizations', 'array-contains', organizationId)
      )
      
      const usersSnapshot = await getDocs(usersQuery)
      const grantPromises = []
      
      for (const userDoc of usersSnapshot.docs) {
        const userId = userDoc.id
        grantPromises.push(
          this.grantGroupAccess(userId, organizationId, groupId, groupName, 'organization_admin')
        )
      }
      
      await Promise.all(grantPromises)
      console.log(`✅ Group access granted to ${usersSnapshot.docs.length} organization followers`)
      
      return usersSnapshot.docs.length
    } catch (error) {
      console.error('❌ Error granting access to all org users:', error)
      throw error
    }
  }

  // Revoke access from all users for a group (useful when making a group private)
  async revokeAccessFromAllUsers(organizationId, groupId) {
    try {
      const accessQuery = query(
        collection(db, this.groupAccessCollection),
        where('organizationId', '==', organizationId),
        where('groupId', '==', groupId),
        where('isActive', '==', true)
      )
      
      const accessSnapshot = await getDocs(accessQuery)
      const revokePromises = []
      
      for (const accessDoc of accessSnapshot.docs) {
        const accessData = accessDoc.data()
        revokePromises.push(
          this.revokeGroupAccess(accessData.userId, organizationId, groupId)
        )
      }
      
      await Promise.all(revokePromises)
      console.log(`✅ Group access revoked from ${accessSnapshot.docs.length} users`)
      
      return accessSnapshot.docs.length
    } catch (error) {
      console.error('❌ Error revoking access from all users:', error)
      throw error
    }
  }
}

export default new GroupAccessService()
